/**
 * Hook for managing projects shared with the current user (recipient view).
 * Handles listing shared projects, pending invitations, and responding to invitations.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { sharingApi } from '@/services/sharingApi';
import type { SharedProject } from '@/lib/types';

interface UseSharingReturn {
  sharedWithMe: SharedProject[];
  pendingInvitations: SharedProject[];
  isLoading: boolean;
  error: string | null;
  acceptInvitation: (shareId: string, projectId: string) => Promise<void>;
  declineInvitation: (shareId: string, projectId: string) => Promise<void>;
  refresh: () => void;
}

export function useSharing(): UseSharingReturn {
  const { isAuthenticated } = useAuthContext();
  const [sharedWithMe, setSharedWithMe] = useState<SharedProject[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<SharedProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSharedWithMe = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await sharingApi.listSharedWithMe();
      setSharedWithMe(result.shares);
      setPendingInvitations(result.pendingInvitations);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load shared projects';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadSharedWithMe();
  }, [loadSharedWithMe]);

  const acceptInvitation = useCallback(
    async (shareId: string, projectId: string) => {
      await sharingApi.respondToShare(shareId, projectId, 'accept');
      await loadSharedWithMe();
    },
    [loadSharedWithMe]
  );

  const declineInvitation = useCallback(
    async (shareId: string, projectId: string) => {
      await sharingApi.respondToShare(shareId, projectId, 'decline');
      await loadSharedWithMe();
    },
    [loadSharedWithMe]
  );

  return {
    sharedWithMe,
    pendingInvitations,
    isLoading,
    error,
    acceptInvitation,
    declineInvitation,
    refresh: loadSharedWithMe,
  };
}
